import React, { useState, useEffect } from 'react';
import { Search, UserX, CheckCircle, Users, Ban, TrendingUp } from 'lucide-react';
import axios from 'axios';
import { Button } from './ui/button';
import StatsCard from './StatsCard';
import { LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const UserManagement = ({ API_BASE }) => {
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showBanModal, setShowBanModal] = useState(false);
  const [banReason, setBanReason] = useState('');
  const [banDuration, setBanDuration] = useState('');

  useEffect(() => {
    fetchUsers();
  }, [searchTerm, statusFilter]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE}/api/admin/users`, {
        params: { search: searchTerm, status: statusFilter }
      });
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBanUser = async () => {
    if (!banReason.trim()) {
      alert('Please provide a reason for ban/suspension');
      return;
    }

    try {
      const duration = banDuration === 'permanent' ? null : parseInt(banDuration);
      await axios.post(`${API_BASE}/api/admin/users/${selectedUser.id}/ban`, null, {
        params: { reason: banReason, duration }
      });
      
      alert(`User ${duration ? 'suspended' : 'banned'} successfully!`);
      setShowBanModal(false);
      setBanReason('');
      setBanDuration('');
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      console.error('Error banning user:', error);
      alert('Failed to ban user');
    }
  };

  const handleUnbanUser = async (userId) => {
    if (!window.confirm('Are you sure you want to reactivate this user?')) return;

    try {
      await axios.post(`${API_BASE}/api/admin/users/${userId}/unban`);
      alert('User reactivated successfully!');
      fetchUsers();
    } catch (error) {
      console.error('Error unbanning user:', error);
      alert('Failed to unban user');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: 'bg-green-500/20 text-green-400',
      banned: 'bg-red-500/20 text-red-400',
      suspended: 'bg-yellow-500/20 text-yellow-400'
    };
    return badges[status] || badges.active;
  };

  // Calculate user statistics
  const calculateStats = () => {
    const totalUsers = users.length;
    const activeUsers = users.filter(u => u.status === 'active').length;
    const bannedUsers = users.filter(u => u.status === 'banned').length;
    const suspendedUsers = users.filter(u => u.status === 'suspended').length;
    
    // Calculate growth (users created in last 30 days vs previous 30 days)
    const now = new Date();
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    const sixtyDaysAgo = new Date(now.getTime() - 60 * 24 * 60 * 60 * 1000);
    
    const recentUsers = users.filter(u => new Date(u.created_at) >= thirtyDaysAgo).length;
    const previousUsers = users.filter(u => {
      const createdDate = new Date(u.created_at);
      return createdDate >= sixtyDaysAgo && createdDate < thirtyDaysAgo;
    }).length;
    
    const growthPercent = previousUsers > 0 
      ? Math.round(((recentUsers - previousUsers) / previousUsers) * 100)
      : recentUsers > 0 ? 100 : 0;
    
    return {
      totalUsers,
      activeUsers,
      bannedUsers,
      suspendedUsers,
      growthPercent,
      recentUsers
    };
  };

  // Prepare user growth data for chart
  const getUserGrowthData = () => {
    const monthlyData = {};
    
    users.forEach(user => {
      const date = new Date(user.created_at);
      const monthYear = date.toLocaleDateString('en', { month: 'short', year: '2-digit' });
      monthlyData[monthYear] = (monthlyData[monthYear] || 0) + 1;
    });
    
    // Get last 6 months
    const result = [];
    const now = new Date();
    for (let i = 5; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthYear = date.toLocaleDateString('en', { month: 'short', year: '2-digit' });
      result.push({
        month: monthYear,
        users: monthlyData[monthYear] || 0
      });
    }
    
    return result;
  };

  // Prepare status distribution data for pie chart
  const getStatusDistribution = () => {
    const stats = calculateStats();
    return [
      { name: 'Active', value: stats.activeUsers, color: '#10B981' },
      { name: 'Suspended', value: stats.suspendedUsers, color: '#F59E0B' },
      { name: 'Banned', value: stats.bannedUsers, color: '#EF4444' }
    ];
  };

  const stats = calculateStats();
  const growthData = getUserGrowthData();
  const statusData = getStatusDistribution();

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="heading-2 text-white">User Management</h2>
        <div className="text-gray-400">Total Users: {users.length}</div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatsCard
          title="Total Users"
          value={stats.totalUsers}
          icon={<Users size={24} />}
          color="blue"
        />
        <StatsCard
          title="Active Users"
          value={stats.activeUsers}
          icon={<CheckCircle size={24} />}
          color="green"
        />
        <StatsCard
          title="Banned/Suspended"
          value={stats.bannedUsers + stats.suspendedUsers}
          icon={<Ban size={24} />}
          color="red"
        />
        <StatsCard
          title="30-Day Growth"
          value={`${stats.growthPercent > 0 ? '+' : ''}${stats.growthPercent}%`}
          icon={<TrendingUp size={24} />}
          color="purple"
          trend={`+${stats.recentUsers} new users`}
        />
      </div>

      {/* Charts Section */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* User Growth Chart */}
        <div className="bg-gray-900/50 border border-turquoise-500/20 rounded-16 p-6">
          <h3 className="text-white text-lg font-semibold mb-4">User Growth Trend</h3>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={growthData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="month" stroke="#9CA3AF" style={{ fontSize: '12px' }} />
              <YAxis stroke="#9CA3AF" style={{ fontSize: '12px' }} />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#1F2937', 
                  border: '1px solid #374151',
                  borderRadius: '8px',
                  color: '#fff'
                }}
              />
              <Line 
                type="monotone" 
                dataKey="users" 
                stroke="#14B8A6" 
                strokeWidth={3}
                dot={{ fill: '#14B8A6', r: 4 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Status Distribution Chart */}
        <div className="bg-gray-900/50 border border-turquoise-500/20 rounded-16 p-6">
          <h3 className="text-white text-lg font-semibold mb-4">User Status Distribution</h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={statusData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {statusData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#1F2937', 
                  border: '1px solid #374151',
                  borderRadius: '8px',
                  color: '#fff'
                }}
              />
              <Legend 
                wrapperStyle={{ color: '#9CA3AF' }}
                iconType="circle"
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex gap-4">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-3 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 p-3 bg-gray-800 border border-gray-700 rounded-lg text-white"
            />
          </div>
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="p-3 bg-gray-800 border border-gray-700 rounded-lg text-white"
        >
          <option value="all">All Users</option>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
          <option value="banned">Banned</option>
        </select>
      </div>

      <div className="bg-gray-900/50 border border-turquoise-500/20 rounded-16 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-800">
            <tr>
              <th className="text-left p-4 text-gray-300">User</th>
              <th className="text-left p-4 text-gray-300">Email</th>
              <th className="text-left p-4 text-gray-300">Joined</th>
              <th className="text-left p-4 text-gray-300">Status</th>
              <th className="text-left p-4 text-gray-300">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="5" className="text-center p-8 text-gray-400">
                  Loading users...
                </td>
              </tr>
            ) : users.length === 0 ? (
              <tr>
                <td colSpan="5" className="text-center p-8 text-gray-400">
                  No users found
                </td>
              </tr>
            ) : (
              users.map((user) => (
                <tr key={user.id} className="border-t border-gray-800 hover:bg-gray-800/30">
                  <td className="p-4">
                    <div className="text-white font-medium">
                      {user.first_name} {user.last_name}
                    </div>
                  </td>
                  <td className="p-4 text-gray-300">{user.email}</td>
                  <td className="p-4 text-gray-400">
                    {new Date(user.created_at).toLocaleDateString()}
                  </td>
                  <td className="p-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadge(user.status)}`}>
                      {user.status}
                    </span>
                    {user.ban_reason && (
                      <div className="text-xs text-gray-500 mt-1">{user.ban_reason}</div>
                    )}
                  </td>
                  <td className="p-4">
                    <div className="flex gap-2">
                      {user.status === 'active' ? (
                        <button
                          onClick={() => {
                            setSelectedUser(user);
                            setShowBanModal(true);
                          }}
                          className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg"
                          title="Ban/Suspend"
                        >
                          <UserX size={16} />
                        </button>
                      ) : (
                        <button
                          onClick={() => handleUnbanUser(user.id)}
                          className="p-2 text-green-400 hover:bg-green-500/10 rounded-lg"
                          title="Reactivate"
                        >
                          <CheckCircle size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showBanModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-gray-900 border border-gray-700 rounded-16 p-6 max-w-md w-full">
            <h3 className="heading-3 text-white mb-4">Ban/Suspend User</h3>
            <p className="text-gray-400 mb-4">
              {selectedUser.first_name} {selectedUser.last_name} ({selectedUser.email})
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Duration</label>
                <select
                  value={banDuration}
                  onChange={(e) => setBanDuration(e.target.value)}
                  className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-white"
                >
                  <option value="">Select duration...</option>
                  <option value="7">7 Days (Temporary Suspension)</option>
                  <option value="30">30 Days (Temporary Suspension)</option>
                  <option value="90">90 Days (Temporary Suspension)</option>
                  <option value="permanent">Permanent Ban</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Reason</label>
                <textarea
                  value={banReason}
                  onChange={(e) => setBanReason(e.target.value)}
                  placeholder="Explain why this user is being banned/suspended..."
                  rows="4"
                  className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-white"
                  dir="ltr"
                  style={{ direction: 'ltr' }}
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button
                onClick={handleBanUser}
                className="btn-primary-enhanced flex-1"
                disabled={!banDuration || !banReason.trim()}
              >
                Confirm
              </Button>
              <Button
                onClick={() => {
                  setShowBanModal(false);
                  setSelectedUser(null);
                  setBanReason('');
                  setBanDuration('');
                }}
                className="btn-secondary flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
